/**
 * Supabase client singleton for Heyward frontend.
 *
 * ADR-016 Sprint C1. Used only for the reactivity layer (realtime
 * subscriptions on deal_events + deal_insights_cache). All mutating
 * API calls still go through FastAPI via authFetch. This client is
 * READ-ONLY from the frontend's perspective — RLS enforces that and
 * the backend (service_role) owns all writes.
 *
 * Auth model:
 * - Clerk owns user identity.
 * - On Clerk login, we call `/api/v1/auth/supabase-token` which returns
 *   a short-lived (1h) JWT signed with SUPABASE_JWT_SECRET.
 * - `useSupabaseAuth` hook installs that JWT here via
 *   `setSupabaseSession`, and refreshes it every ~50min.
 * - Supabase realtime + PostgREST verify the JWT, extract `sub`, and
 *   apply the RLS policies (user can only see rows for deals.user_id
 *   matching their sub).
 *
 * Without a session token, the client operates as anon — which sees
 * zero rows under the RLS policies. That's the safe default.
 */

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Loud in dev so misconfig surfaces on first boot instead of silently
  // leaving realtime dead and making it look like a feature bug.
  // eslint-disable-next-line no-console
  console.warn(
    '[supabaseClient] REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY not set — ' +
    'realtime subscriptions will be disabled. Check frontend/.env.local'
  );
}

export const supabase = createClient(
  SUPABASE_URL || 'https://missing.supabase.co',
  SUPABASE_ANON_KEY || 'missing',
  {
    auth: {
      // We don't use Supabase's own auth — Clerk is the identity source.
      // Disable auto-refresh + persistence so Supabase doesn't try to
      // manage its own session state.
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    realtime: {
      // Slightly higher event rate than default — Progress Ledger can
      // see bursts when the orchestrator finishes a pipeline run.
      params: { eventsPerSecond: 20 },
    },
  }
);

/**
 * Install a user-scoped JWT on the Supabase client so realtime +
 * PostgREST apply RLS as this user. Called by useSupabaseAuth after
 * the Clerk→Supabase bridge mints a token.
 */
export function setSupabaseSession(accessToken) {
  if (!accessToken) return;
  supabase.realtime.setAuth(accessToken);
  // setSession sets the JWT used for REST requests. We pass refresh as
  // empty because we manage refresh ourselves via useSupabaseAuth.
  // If Supabase rejects the synthetic session shape, realtime.setAuth
  // alone still authenticates subscriptions — but we warn because it
  // means PostgREST calls from the supabase client will fall back to
  // anon (RLS-denied for user rows).
  supabase.auth.setSession({ access_token: accessToken, refresh_token: '' }).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn(
      '[supabaseClient] setSession rejected — realtime is still authed, ' +
      'but REST requests via the supabase client will use anon:',
      err
    );
  });
}

/**
 * Clear the session on Clerk logout so orphaned realtime channels
 * can't leak data across user switches in the same tab.
 */
export function clearSupabaseSession() {
  supabase.realtime.setAuth(null);
  supabase.auth.signOut().catch((err) => {
    // eslint-disable-next-line no-console
    console.warn('[supabaseClient] signOut failed — realtime auth cleared anyway:', err);
  });
}
